const TIMEOUT = 5000 // 5 seconds
const CHECK_LIMIT = 60 // 5 minutes at 5 second interval

export interface UseAutoMatchItemsOptions {
  multiMatch?: Ref<boolean | undefined> | boolean
  onCount?: (count: number) => void
  onItems: (items: any[]) => void
}

export default function useAutoMatchItems(
  items: Ref<any[]>,
  orderItems: Ref<any[]>,
  options: UseAutoMatchItemsOptions,
) {
  const { $api, $error, $toast } = useNuxtApp()

  const loading = ref(false)
  const cancelled = ref(false)
  const isMultiMatch = () => !!unref(options.multiMatch)

  let timeout: ReturnType<typeof setTimeout>
  let checkCount = 0

  onBeforeUnmount(() => clearTimeout(timeout))

  // ponytail: cancel only stops client-side polling — there's no backend endpoint to cancel
  // the queued job itself. Add one if a cancelled match is ever found to apply after the fact.
  const cancel = () => {
    cancelled.value = true
    clearTimeout(timeout)
    loading.value = false
  }

  const finish = (count: number) => {
    loading.value = false

    if (count > 0) {
      $toast.success(`${count} item${count > 1 ? 's' : ''} matched`)
    }
    else {
      $toast.warning("No matches found. You'll need to match the items yourself.")
    }

    options.onCount?.(count)
  }

  // Only send unmatched items to the job and only ever write onto those — existing matches
  // (manual or from a prior run) are never sent back for re-matching or overwritten.
  const applyDuplicateGuard = (updatedItems: any[]) => {
    if (isMultiMatch()) {
      return { items: updatedItems, removed: 0 }
    }

    const seen = new Set<string>()
    let removed = 0

    const deduped = updatedItems.map((item) => {
      if (!item.reference_identifier) {
        return item
      }

      if (seen.has(item.reference_identifier)) {
        removed += 1
        return { ...item, reference_identifier: '' }
      }

      seen.add(item.reference_identifier)
      return item
    })

    return { items: deduped, removed }
  }

  const singleMatch = (currentItems: any[], unmatchedNumbers: Set<number>, targetPoItem: any) => {
    const updated = currentItems.map((item) => {
      if (!unmatchedNumbers.has(item.item_number)) {
        return item
      }

      return {
        ...item,
        reference_identifier: targetPoItem.internal_identifier,
        cost_code: targetPoItem.cost_code ?? '',
        tax: item.tax || (targetPoItem.tax ?? null),
      }
    })

    const { items: deduped, removed } = applyDuplicateGuard(updated)

    options.onItems(deduped)
    finish(unmatchedNumbers.size - removed)
  }

  const getJob = async (job: string, currentItems: any[], orderItemsList: any[]) => {
    if (cancelled.value) {
      return
    }

    try {
      const response = await $api.v1.jobs.get(job)

      if (cancelled.value) {
        return
      }

      if (['created', 'queued', 'started'].includes(response.status) && checkCount < CHECK_LIMIT) {
        checkCount += 1
        timeout = setTimeout(() => getJob(job, currentItems, orderItemsList), TIMEOUT)
        return
      }

      if (response.status !== 'finished') {
        finish(0)
        return
      }

      let count = 0
      const matchedItems = response.result?.matched_items || []
      const updatedItems = currentItems.map((item: any) => ({ ...item }))

      matchedItems.forEach((matchedItem: any) => {
        if (!matchedItem.po_id) {
          return
        }

        const matchedPoItem = orderItemsList.find((poItem: any) => poItem.id === Number(matchedItem.po_id))
        const updatedItem = updatedItems.find(
          (item: any) => item.item_number === Number(matchedItem.item_number)
        )

        if (!matchedPoItem || !updatedItem) {
          return
        }

        updatedItem.reference_identifier = matchedPoItem.internal_identifier
        updatedItem.cost_code = matchedPoItem.cost_code ?? ''
        if (!updatedItem.tax) {
          updatedItem.tax = matchedPoItem.tax ?? null
        }
        updatedItem.exception = matchedItem.exception ?? ''
        count += 1
      })

      const { items: deduped, removed } = applyDuplicateGuard(updatedItems)

      options.onItems(deduped)
      finish(Math.max(0, count - removed))
    }
    catch (error) {
      console.error(error)
      finish(0)
    }
  }

  const autoMatch = async (currentItems: any[], unmatched: any[], orderItemsList: any[]) => {
    loading.value = true

    try {
      const { job } = await $api.v1.queueMatchItems({
        items: unmatched.map((item) => ({
          item_number: item.item_number,
          description: item.description,
          rental_duration: item.rental_duration,
          rental_duration_unit: item.rental_duration_unit,
          unit_price: item.unit_price,
          quantity: item.quantity,
          day_rate: item.day_rate,
          week_rate: item.week_rate,
          month_rate: item.month_rate,
        })),
        items_to_match: orderItemsList.map((sku) => ({
          item_number: sku.id,
          description: sku.description,
          rental_duration: sku.rental_duration,
          rental_duration_unit: sku.rental_duration_unit,
          unit_price: sku.unit_price,
          quantity: sku.quantity,
          day_rate: sku.day_rate,
          week_rate: sku.week_rate,
          month_rate: sku.month_rate,
        })),
      })

      timeout = setTimeout(() => getJob(job, currentItems, orderItemsList), TIMEOUT)
    }
    catch (error) {
      $error.report(error)
      finish(0)
    }
  }

  const run = () => {
    cancelled.value = false
    checkCount = 0

    const currentItems = items.value.map((item, i) => ({ ...item, item_number: i + 1 }))
    const orderItemsList = orderItems.value
    const unmatched = currentItems.filter(item => !item.reference_identifier)

    if (unmatched.length === 0 || orderItemsList.length === 0) {
      return
    }

    if (
      (orderItemsList.length === 1 && unmatched.length === 1)
      || (isMultiMatch() && orderItemsList.length === 1)
    ) {
      singleMatch(currentItems, new Set(unmatched.map(item => item.item_number)), orderItemsList[0])
      return
    }

    autoMatch(currentItems, unmatched, orderItemsList)
  }

  return { loading, cancel, run }
}
